import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { StartupOpportunityUtils } from '../../../../utils/startupOpportunityModels';
import { createStartupOpportunity, updateStartupOpportunity } from '../../../../services/firebase/startup-opportunities';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import CompanySearch from '../../../features/onboarding/CompanySearch';
import CompanyFormModal from '../../../admin/company/CompanyFormModal';

// Render CompanyFormModal in a Portal to avoid stacking context issues
function CompanyFormModalPortal({ isOpen, onClose, onCreate, loading }) {
  return ReactDOM.createPortal(
    <CompanyFormModal 
      isOpen={isOpen}
      onClose={onClose}
      onCreate={onCreate}
      loading={loading}
      context="opportunity"
    />,
    document.body
  );
}

const OpportunityFormModal = ({ 
  isOpen, 
  onClose, 
  opportunity = null, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    companyId: '', // Added for company reference
    companyName: '',
    companyDescription: '',
    // New company fields
    companyLogo: '',
    companyLocation: '',
    companyWebsite: '',
    companyIndustry: '',
    companySize: '',
    companyFoundedYear: '',
    // Opportunity fields
    fundingStage: '',
    fundingAmount: '',
    leadInvestors: [],
    roleArea: '',
    roleDescription: '',
    experienceLevel: '',
    isFlexible: true,
    highlights: [],
    founderInfo: {
      name: '',
      title: '',
      bio: ''
    },
    companyMetrics: {
      teamSize: '',
      clientCount: '',
      growth: ''
    },
    requirements: [],
    benefits: [],
    applicationProcess: '',
    status: 'active'
  });

  // Track additional company details from database
  const [selectedCompanyDetails, setSelectedCompanyDetails] = useState(null);
  const [isCompanyFromDatabase, setIsCompanyFromDatabase] = useState(false);
  
  // Company modal state
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [companyModalLoading, setCompanyModalLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentHighlight, setCurrentHighlight] = useState('');
  const [currentInvestor, setCurrentInvestor] = useState('');
  const [currentRequirement, setCurrentRequirement] = useState('');
  const [currentBenefit, setCurrentBenefit] = useState('');

  // Initialize form data when opportunity prop changes
  useEffect(() => {
    const initializeForm = async () => {
      if (opportunity) {
        setFormData({
          companyId: opportunity.companyId || '', // Initialize companyId from opportunity
          companyName: opportunity.companyName || '',
          companyDescription: opportunity.companyDescription || '',
          // New company fields
          companyLogo: opportunity.companyLogo || '',
          companyLocation: opportunity.companyLocation || '',
          companyWebsite: opportunity.companyWebsite || '',
          companyIndustry: opportunity.companyIndustry || '',
          companySize: opportunity.companySize || '',
          companyFoundedYear: opportunity.companyFoundedYear || '',
          // Opportunity fields
          fundingStage: opportunity.fundingStage || '',
          fundingAmount: opportunity.fundingAmount || '',
          leadInvestors: opportunity.leadInvestors || [],
          roleArea: opportunity.roleArea || '',
          roleDescription: opportunity.roleDescription || '',
          experienceLevel: opportunity.experienceLevel || '',
          isFlexible: opportunity.isFlexible !== undefined ? opportunity.isFlexible : true,
          highlights: opportunity.highlights || [],
          founderInfo: {
            name: opportunity.founderInfo?.name || '',
            title: opportunity.founderInfo?.title || '',
            bio: opportunity.founderInfo?.bio || ''
          },
          companyMetrics: {
            teamSize: opportunity.companyMetrics?.teamSize || '',
            clientCount: opportunity.companyMetrics?.clientCount || '',
            growth: opportunity.companyMetrics?.growth || ''
          },
          requirements: opportunity.requirements || [],
          benefits: opportunity.benefits || [],
          applicationProcess: opportunity.applicationProcess || '',
          status: opportunity.status || 'active'
        });

        // Logo preview is handled by the separate modal

        // Load company details if companyId exists
        if (opportunity.companyId) {
          const companyDetails = await fetchCompanyDetails(opportunity.companyId);
          if (companyDetails) {
            setSelectedCompanyDetails(companyDetails);
            setIsCompanyFromDatabase(true);
          }
        } else {
          setSelectedCompanyDetails(null);
          setIsCompanyFromDatabase(false);
        }
      } else {
        // Reset form for new opportunity
        setFormData({
          companyId: '', // Reset companyId
          companyName: '',
          companyDescription: '',
          // New company fields
          companyLogo: '',
          companyLocation: '',
          companyWebsite: '',
          companyIndustry: '',
          companySize: '',
          companyFoundedYear: '',
          fundingStage: '',
          fundingAmount: '',
          leadInvestors: [],
          roleArea: '',
          roleDescription: '',
          experienceLevel: '',
          isFlexible: true,
          highlights: [],
          founderInfo: {
            name: '',
            title: '',
            bio: ''
          },
          companyMetrics: {
            teamSize: '',
            clientCount: '',
            growth: ''
          },
          requirements: [],
          benefits: [],
          applicationProcess: '',
          status: 'active'
        });
        setSelectedCompanyDetails(null);
        setIsCompanyFromDatabase(false);
      }
      setErrors({});
    };
    
    initializeForm();
  }, [opportunity]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const addToArray = (arrayField, value, currentValueSetter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [arrayField]: [...prev[arrayField], value.trim()]
      }));
      currentValueSetter('');
    }
  };

  const removeFromArray = (arrayField, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayField]: prev[arrayField].filter((_, i) => i !== index)
    }));
  };

  // Fetch full company details from database
  const fetchCompanyDetails = async (companyId) => {
    try {
      const db = getFirestore();
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await getDoc(companyRef);
      
      if (companySnap.exists()) {
        return companySnap.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching company details:', error);
      return null;
    }
  };

  // Handle company creation from modal
  const handleCompanyCreate = async (companyData) => {
    setCompanyModalLoading(true);
    try {
      // Import the createCompany service
      const { createCompany } = await import('../../../../services/firebase/companies');
      
      // Create the company using the service
      const result = await createCompany(companyData);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      const createdCompany = result.data;
      
      // Update our form with the created company data
      setFormData(prev => ({
        ...prev,
        companyId: createdCompany.id,
        companyName: createdCompany.name,
        companyDescription: createdCompany.description || '',
        companyLogo: createdCompany.logo || '',
        companyLocation: createdCompany.location || '',
        companyWebsite: createdCompany.website || '',
        companyIndustry: createdCompany.industry || '',
        companySize: createdCompany.companySize || '',
        companyFoundedYear: createdCompany.foundedYear || ''
      }));
      
      setSelectedCompanyDetails(createdCompany);
      setIsCompanyFromDatabase(true);
      setIsCompanyModalOpen(false);
      
      // Clear any existing company errors
      if (errors.companyName) {
        setErrors(prev => ({
          ...prev,
          companyName: null
        }));
      }
    } catch (error) {
      console.error('Error handling company creation:', error);
      setErrors(prev => ({
        ...prev,
        companyName: error.message || 'Failed to create company'
      }));
    } finally {
      setCompanyModalLoading(false);
    }
  };

  // Handle company selection from CompanySearch component
  const handleCompanySelect = async (company) => {
    if (company) {
      // Fetch full company details
      const fullCompanyDetails = await fetchCompanyDetails(company.id);
      
      setFormData(prev => ({
        ...prev,
        companyId: company.id,
        companyName: company.name,
        companyDescription: fullCompanyDetails?.description || '',
        // Populate additional company fields from database
        companyLogo: fullCompanyDetails?.logo || '',
        companyLocation: fullCompanyDetails?.location || '',
        companyWebsite: fullCompanyDetails?.website || '',
        companyIndustry: fullCompanyDetails?.industry || '',
        companySize: fullCompanyDetails?.companySize || '',
        companyFoundedYear: fullCompanyDetails?.foundedYear || ''
      }));
      
      setSelectedCompanyDetails(fullCompanyDetails);
      setIsCompanyFromDatabase(true);
      
      // Clear any existing company errors
      if (errors.companyName) {
        setErrors(prev => ({
          ...prev,
          companyName: null
        }));
      }
    } else {
      // No company selected - reset form
      setSelectedCompanyDetails(null);
      setIsCompanyFromDatabase(false);
      setFormData(prev => ({
        ...prev,
        companyId: '',
        companyName: '',
        companyDescription: '',
        companyLogo: '',
        companyLocation: '',
        companyWebsite: '',
        companyIndustry: '',
        companySize: '',
        companyFoundedYear: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData.companyDescription.trim()) {
      newErrors.companyDescription = 'Company description is required';
    }



    if (!formData.fundingStage) {
      newErrors.fundingStage = 'Funding stage is required';
    }

    if (!formData.roleArea) {
      newErrors.roleArea = 'Role area is required';
    }

    if (!formData.roleDescription.trim()) {
      newErrors.roleDescription = 'Role description is required';
    }

    if (!formData.experienceLevel) {
      newErrors.experienceLevel = 'Experience level is required';
    }

    // Validate founder info if any field is filled
    const founderFields = Object.values(formData.founderInfo).filter(val => val.trim());
    if (founderFields.length > 0) {
      if (!formData.founderInfo.name.trim()) {
        newErrors['founderInfo.name'] = 'Founder name is required when founder info is provided';
      }
      if (!formData.founderInfo.title.trim()) {
        newErrors['founderInfo.title'] = 'Founder title is required when founder info is provided';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for submission
      const submissionData = {
        ...formData,
        // companyId is already in formData
        // Only include founderInfo if at least name and title are provided
        founderInfo: formData.founderInfo.name.trim() && formData.founderInfo.title.trim() 
          ? formData.founderInfo 
          : null,
        // Only include companyMetrics if at least one field is provided
        companyMetrics: Object.values(formData.companyMetrics).some(val => val.toString().trim())
          ? {
              teamSize: formData.companyMetrics.teamSize ? parseInt(formData.companyMetrics.teamSize) : null,
              clientCount: formData.companyMetrics.clientCount ? parseInt(formData.companyMetrics.clientCount) : null,
              growth: formData.companyMetrics.growth.trim() || null
            }
          : null
      };

      let result;
      if (opportunity) {
        // Update existing opportunity
        result = await updateStartupOpportunity(opportunity.id, submissionData);
      } else {
        // Create new opportunity
        result = await createStartupOpportunity(submissionData);
      }

      if (result.success) {
        onSuccess?.(result.data);
        onClose();
      } else {
        if (result.validationErrors) {
          const fieldErrors = {};
          result.validationErrors.forEach(error => {
            // Map validation errors to form fields
            if (error.includes('Company name')) fieldErrors.companyName = error;
            else if (error.includes('Company description')) fieldErrors.companyDescription = error;
            else if (error.includes('Role area')) fieldErrors.roleArea = error;
            else if (error.includes('Role description')) fieldErrors.roleDescription = error;
            else if (error.includes('Funding stage')) fieldErrors.fundingStage = error;
            else if (error.includes('Experience level')) fieldErrors.experienceLevel = error;
            else if (error.includes('Founder name')) fieldErrors['founderInfo.name'] = error;
            else if (error.includes('Founder title')) fieldErrors['founderInfo.title'] = error;
          });
          setErrors(fieldErrors);
        } else {
          setErrors({ submit: result.error });
        }
      }
    } catch (error) {
      console.error('Error submitting opportunity:', error);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {opportunity ? 'Edit Opportunity' : 'Create New Opportunity'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Information */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Company Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company *
                  </label>
                  <div className={`${errors.companyName ? 'ring-2 ring-red-500 rounded-lg' : ''}`}>
                    <CompanySearch
                      onCompanySelect={handleCompanySelect}
                      selectedCompanyId={formData.companyId}
                      selectedCompanyName={formData.companyName}
                    />
                  </div>
                  {isCompanyFromDatabase && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => handleCompanySelect(null)}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                      >
                        Clear selection & enter company manually
                      </button>
                    </div>
                  )}
                  {errors.companyName && (
                    <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Funding Stage *
                  </label>
                  <select
                    value={formData.fundingStage}
                    onChange={(e) => handleInputChange('fundingStage', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white ${
                      errors.fundingStage ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select funding stage</option>
                    {StartupOpportunityUtils.getFundingStages().map(stage => (
                      <option key={stage} value={stage}>
                        {StartupOpportunityUtils.formatFundingStage(stage)}
                      </option>
                    ))}
                  </select>
                  {errors.fundingStage && (
                    <p className="mt-1 text-sm text-red-600">{errors.fundingStage}</p>
                  )}
                </div>
              </div>

              {/* Add Company Button - Show when no company is selected */}
              {!isCompanyFromDatabase && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setIsCompanyModalOpen(true)}
                    className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-500 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:text-blue-400 transition-colors"
                  >
                    + Add New Company
                  </button>
                </div>
              )}

              {/* Company Description - Show when from database */}
              {isCompanyFromDatabase && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company Description *
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                      (Auto-populated from database)
                    </span>
                  </label>
                  <textarea
                    value={formData.companyDescription}
                    onChange={(e) => handleInputChange('companyDescription', e.target.value)}
                    rows={4}
                    readOnly={true}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white border-gray-300 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                    placeholder="Company description loaded from database"
                  />
                  {errors.companyDescription && (
                    <p className="mt-1 text-sm text-red-600">{errors.companyDescription}</p>
                  )}
                </div>
              )}

              {/* Additional Company Information (Read-only when from database) */}
              {isCompanyFromDatabase && selectedCompanyDetails && (
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-3">
                    Company Information (from database)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {selectedCompanyDetails.website && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Website:</span>
                        <div className="text-gray-600 dark:text-gray-400">{selectedCompanyDetails.website}</div>
                      </div>
                    )}
                    {selectedCompanyDetails.industry && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Industry:</span>
                        <div className="text-gray-600 dark:text-gray-400">{selectedCompanyDetails.industry}</div>
                      </div>
                    )}
                    {selectedCompanyDetails.location && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Location:</span>
                        <div className="text-gray-600 dark:text-gray-400">{selectedCompanyDetails.location}</div>
                      </div>
                    )}
                    {selectedCompanyDetails.companySize && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Company Size:</span>
                        <div className="text-gray-600 dark:text-gray-400">{selectedCompanyDetails.companySize}</div>
                      </div>
                    )}
                    {selectedCompanyDetails.foundedYear && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Founded:</span>
                        <div className="text-gray-600 dark:text-gray-400">{selectedCompanyDetails.foundedYear}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Funding Amount
                </label>
                <input
                  type="text"
                  value={formData.fundingAmount}
                  onChange={(e) => handleInputChange('fundingAmount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  placeholder="e.g., $5M, $18M, etc."
                />
              </div>
            </div>      
      {/* Lead Investors */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Lead Investors</h3>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={currentInvestor}
                  onChange={(e) => setCurrentInvestor(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  placeholder="Enter investor name"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addToArray('leadInvestors', currentInvestor, setCurrentInvestor);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => addToArray('leadInvestors', currentInvestor, setCurrentInvestor)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Add
                </button>
              </div>

              {formData.leadInvestors.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.leadInvestors.map((investor, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    >
                      {investor}
                      <button
                        type="button"
                        onClick={() => removeFromArray('leadInvestors', index)}
                        className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Role Information */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Role Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role Area *
                  </label>
                  <select
                    value={formData.roleArea}
                    onChange={(e) => handleInputChange('roleArea', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white ${
                      errors.roleArea ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select role area</option>
                    {StartupOpportunityUtils.getRoleAreas().map(area => (
                      <option key={area} value={area}>
                        {StartupOpportunityUtils.formatRoleArea(area)}
                      </option>
                    ))}
                  </select>
                  {errors.roleArea && (
                    <p className="mt-1 text-sm text-red-600">{errors.roleArea}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Experience Level *
                  </label>
                  <select
                    value={formData.experienceLevel}
                    onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white ${
                      errors.experienceLevel ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select experience level</option>
                    {StartupOpportunityUtils.getExperienceLevels().map(level => (
                      <option key={level} value={level}>
                        {StartupOpportunityUtils.formatExperienceLevel(level)}
                      </option>
                    ))}
                  </select>
                  {errors.experienceLevel && (
                    <p className="mt-1 text-sm text-red-600">{errors.experienceLevel}</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role Description *
                </label>
                <textarea
                  value={formData.roleDescription}
                  onChange={(e) => handleInputChange('roleDescription', e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white ${
                    errors.roleDescription ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Describe the role, responsibilities, and what the candidate will work on..."
                />
                {errors.roleDescription && (
                  <p className="mt-1 text-sm text-red-600">{errors.roleDescription}</p>
                )}
              </div>

              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isFlexible}
                    onChange={(e) => handleInputChange('isFlexible', e.target.checked)}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    This is a flexible role (title and responsibilities may evolve)
                  </span>
                </label>
              </div>
            </div>

            {/* Highlights */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Highlights</h3>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={currentHighlight}
                  onChange={(e) => setCurrentHighlight(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  placeholder="Enter a key highlight or selling point"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addToArray('highlights', currentHighlight, setCurrentHighlight);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => addToArray('highlights', currentHighlight, setCurrentHighlight)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Add
                </button>
              </div>

              {formData.highlights.length > 0 && (
                <div className="space-y-2">
                  {formData.highlights.map((highlight, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white dark:bg-gray-600 rounded border"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">{highlight}</span>
                      <button
                        type="button"
                        onClick={() => removeFromArray('highlights', index)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Founder Information */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Founder Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Founder Name
                  </label>
                  <input
                    type="text"
                    value={formData.founderInfo.name}
                    onChange={(e) => handleNestedInputChange('founderInfo', 'name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white ${
                      errors['founderInfo.name'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter founder's name"
                  />
                  {errors['founderInfo.name'] && (
                    <p className="mt-1 text-sm text-red-600">{errors['founderInfo.name']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Founder Title
                  </label>
                  <input
                    type="text"
                    value={formData.founderInfo.title}
                    onChange={(e) => handleNestedInputChange('founderInfo', 'title', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white ${
                      errors['founderInfo.title'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., CEO, Co-founder"
                  />
                  {errors['founderInfo.title'] && (
                    <p className="mt-1 text-sm text-red-600">{errors['founderInfo.title']}</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Founder Bio
                </label>
                <textarea
                  value={formData.founderInfo.bio}
                  onChange={(e) => handleNestedInputChange('founderInfo', 'bio', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  placeholder="Brief background about the founder..."
                />
              </div>
            </div>

            {/* Company Metrics */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Company Metrics</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Team Size
                  </label>
                  <input
                    type="number"
                    value={formData.companyMetrics.teamSize}
                    onChange={(e) => handleNestedInputChange('companyMetrics', 'teamSize', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    placeholder="e.g., 25"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Client Count
                  </label>
                  <input
                    type="number"
                    value={formData.companyMetrics.clientCount}
                    onChange={(e) => handleNestedInputChange('companyMetrics', 'clientCount', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    placeholder="e.g., 500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Growth Rate
                  </label>
                  <input
                    type="text"
                    value={formData.companyMetrics.growth}
                    onChange={(e) => handleNestedInputChange('companyMetrics', 'growth', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    placeholder="e.g., 20% MoM"
                  />
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Requirements</h3>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={currentRequirement}
                  onChange={(e) => setCurrentRequirement(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  placeholder="Enter a requirement or qualification"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addToArray('requirements', currentRequirement, setCurrentRequirement);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => addToArray('requirements', currentRequirement, setCurrentRequirement)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Add
                </button>
              </div>

              {formData.requirements.length > 0 && (
                <div className="space-y-2">
                  {formData.requirements.map((requirement, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white dark:bg-gray-600 rounded border"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">{requirement}</span>
                      <button
                        type="button"
                        onClick={() => removeFromArray('requirements', index)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Benefits */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Benefits & Perks</h3>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={currentBenefit}
                  onChange={(e) => setCurrentBenefit(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  placeholder="Enter a benefit or perk"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addToArray('benefits', currentBenefit, setCurrentBenefit);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => addToArray('benefits', currentBenefit, setCurrentBenefit)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Add
                </button>
              </div>

              {formData.benefits.length > 0 && (
                <div className="space-y-2">
                  {formData.benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white dark:bg-gray-600 rounded border"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">{benefit}</span>
                      <button
                        type="button"
                        onClick={() => removeFromArray('benefits', index)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Application Process */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Application Process</h3>
              
              <textarea
                value={formData.applicationProcess}
                onChange={(e) => handleInputChange('applicationProcess', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                placeholder="Describe the application and interview process..."
              />
            </div>

            {/* Status */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Status</h3>
              
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="filled">Filled</option>
              </select>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-4">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : (opportunity ? 'Update Opportunity' : 'Create Opportunity')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Company Creation Modal */}
      <CompanyFormModalPortal
        isOpen={isCompanyModalOpen}
        onClose={() => setIsCompanyModalOpen(false)}
        onCreate={handleCompanyCreate}
        loading={companyModalLoading}
      />
    </div>
  );
};

export default OpportunityFormModal;